import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchNewArrivalNotifications } from 'api/new-arrival-notification.service';
import { NewArrivalNotificationData } from 'redux/new-arrival-notification-unread-store/new-arrival-notification-unread-store.slice';
import { NewArrivalNotificationModel } from 'types/entity';

export const NEW_ARRIVAL_NOTIFICATION_READ_STORE_NAME = 'newArrivalNotificationReadStore';

export const loadReadNotifications = createAsyncThunk(
  `${NEW_ARRIVAL_NOTIFICATION_READ_STORE_NAME}/load`,
  async ({ projectId, offset }: { projectId: string; offset: number }) => {
    const response = await fetchNewArrivalNotifications(projectId, offset, 'READ');
    return response;
  },
);

export const loadMoreReadNotifications = createAsyncThunk(
  `${NEW_ARRIVAL_NOTIFICATION_READ_STORE_NAME}/loadMore`,
  async ({ projectId, offset }: { projectId: string; offset: number }) => {
    const response = await fetchNewArrivalNotifications(projectId, offset, 'READ');
    return response;
  },
);

export type NewArrivalNotificationReadStoreState = {
  loaded: boolean;
  readNotifications: NewArrivalNotificationData[];
  currentOffset: number;
  hasMore: boolean;
  loadingMore: boolean;
  error: any;
};

const appendId = (data: NewArrivalNotificationModel[], offset: number): NewArrivalNotificationData[] => data.map((d, i) => ({ ...d, id: offset + i }));

export const initialNewArrivalNotificationReadStoreState: NewArrivalNotificationReadStoreState = {
  loaded: false,
  readNotifications: [],
  currentOffset: 0,
  hasMore: false,
  loadingMore: false,
  error: null,
};

export const newArrivalNotificationReadStoreSlice = createSlice({
  name: NEW_ARRIVAL_NOTIFICATION_READ_STORE_NAME,
  initialState: initialNewArrivalNotificationReadStoreState,
  reducers: {
    resetState: (state) => {
      state.loaded = initialNewArrivalNotificationReadStoreState.loaded;
      state.readNotifications = initialNewArrivalNotificationReadStoreState.readNotifications;
      state.currentOffset = initialNewArrivalNotificationReadStoreState.currentOffset;
      state.hasMore = initialNewArrivalNotificationReadStoreState.hasMore;
      state.loadingMore = initialNewArrivalNotificationReadStoreState.loadingMore;
      state.error = initialNewArrivalNotificationReadStoreState.error;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadReadNotifications.pending, (state) => {
      state.loaded = false;
      state.error = null;
    });
    builder.addCase(loadReadNotifications.fulfilled, (state, action) => {
      state.readNotifications = appendId(action.payload.api_display_values || initialNewArrivalNotificationReadStoreState.readNotifications, 0);
      state.currentOffset = action.payload.offset || initialNewArrivalNotificationReadStoreState.currentOffset;
      state.hasMore = action.payload.additional_search_flag || false;
      state.loaded = true;
    });
    builder.addCase(loadReadNotifications.rejected, (state, action) => {
      state.error = action.payload;
      state.loaded = true;
    });
    builder.addCase(loadMoreReadNotifications.pending, (state) => {
      state.error = null;
      state.loadingMore = true;
    });
    builder.addCase(loadMoreReadNotifications.fulfilled, (state, action) => {
      const newItems = appendId(action.payload.api_display_values || initialNewArrivalNotificationReadStoreState.readNotifications, state.readNotifications.length);
      state.readNotifications = [...state.readNotifications, ...newItems];
      state.currentOffset = action.payload.offset || state.currentOffset;
      state.hasMore = action.payload.additional_search_flag ?? false;
      state.loadingMore = false;
    });
    builder.addCase(loadMoreReadNotifications.rejected, (state, action) => {
      state.error = action.payload;
      state.loadingMore = false;
    });
  },
});

export const newArrivalNotificationReadStoreAction = {
  ...newArrivalNotificationReadStoreSlice.actions,
  loadReadNotifications,
  loadMoreReadNotifications,
};

export default newArrivalNotificationReadStoreSlice.reducer;
